import { masterSupabase } from '../lib/supabase-master.js';

/**
 * Zentraler Activity Service für vollständige Aktivitätsverfolgung
 * Erfasst alle User-Aktionen im Clara360-Portal
 */
class ActivityService {
  
  /**
   * Loggt eine Aktivität in die Datenbank
   * @param {string} activity_type - Aktivitätstyp (bewirtschaftung, objekte, mieter, etc.)
   * @param {string} category - Kategorie (documents, objects, tenants, etc.)
   * @param {string} title - Titel der Aktivität
   * @param {string} description - Beschreibung der Aktivität
   * @param {Object} metadata - Zusätzliche Metadaten
   */
  async logActivity(activity_type, category, title, description, metadata = {}) {
    try {
      const activityData = {
        activity_type,
        category,
        title,
        description,
        user_name: await this.getCurrentUserName(),
        user_id: await this.getCurrentUserId(),
        metadata: JSON.stringify(metadata),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      const { data, error } = await masterSupabase
        .from('activities')
        .insert([activityData])
        .select();

      if (error) {
        console.warn('Fehler beim Loggen der Aktivität:', error, activityData);
        return null;
      }

      console.log('✅ Aktivität geloggt:', activityData);
      return data[0];
    } catch (error) {
      console.error('❌ Fehler beim Loggen der Aktivität:', error);
      return null;
    }
  }

  /**
   * Holt den aktuellen User-Namen
   */
  async getCurrentUserName() {
    try {
      const { data: { user } } = await masterSupabase.auth.getUser();
      return user?.email || 'System';
    } catch (error) {
      return 'System';
    }
  }

  /**
   * Holt die aktuelle User-ID
   */
  async getCurrentUserId() {
    try {
      const { data: { user } } = await masterSupabase.auth.getUser();
      return user?.id || 'system';
    } catch (error) {
      return 'system';
    }
  }

  // === BEWIRTSCHAFTUNG ===
  async logCostCreated(costData) {
    return this.logActivity(
      'cost_created',
      'bewirtschaftung',
      'Neue Kosten angelegt',
      `${costData.description || 'Unbenannt'} (${costData.amount}€)`,
      {
        cost_id: costData.id,
        amount: costData.amount,
        property_id: costData.property_id,
        category: costData.category
      }
    );
  }

  async logCostUpdated(costData) {
    return this.logActivity(
      'cost_updated',
      'bewirtschaftung',
      'Kosten bearbeitet',
      `${costData.description || 'Unbenannt'} (${costData.amount}€)`,
      {
        cost_id: costData.id,
        amount: costData.amount,
        property_id: costData.property_id,
        category: costData.category
      }
    );
  }

  async logCostDeleted(costData) {
    return this.logActivity(
      'cost_deleted',
      'bewirtschaftung',
      'Kosten gelöscht',
      `${costData.description || 'Unbenannt'} (${costData.amount}€)`,
      {
        cost_id: costData.id,
        amount: costData.amount,
        property_id: costData.property_id,
        category: costData.category
      }
    );
  }

  // === OBJEKTE ===
  async logPropertyCreated(propertyData) {
    return this.logActivity(
      'objekte',
      'erstellt',
      `Neues Objekt angelegt: ${propertyData.address || 'Unbenannt'}`,
      {
        property_id: propertyData.id,
        address: propertyData.address,
        type: propertyData.type
      }
    );
  }

  async logPropertyUpdated(propertyData) {
    return this.logActivity(
      'objekte',
      'bearbeitet',
      `Objekt bearbeitet: ${propertyData.address || 'Unbenannt'}`,
      {
        property_id: propertyData.id,
        address: propertyData.address,
        type: propertyData.type
      }
    );
  }

  async logPropertyDeleted(propertyData) {
    return this.logActivity(
      'objekte',
      'gelöscht',
      `Objekt gelöscht: ${propertyData.address || 'Unbenannt'}`,
      {
        property_id: propertyData.id,
        address: propertyData.address,
        type: propertyData.type
      }
    );
  }

  // === EINHEITEN ===
  async logUnitCreated(unitData) {
    return this.logActivity(
      'einheiten',
      'erstellt',
      `Neue Einheit angelegt: ${unitData.unit_number || 'Unbenannt'}`,
      {
        unit_id: unitData.id,
        unit_number: unitData.unit_number,
        property_id: unitData.property_id
      }
    );
  }

  async logUnitUpdated(unitData) {
    return this.logActivity(
      'einheiten',
      'bearbeitet',
      `Einheit bearbeitet: ${unitData.unit_number || 'Unbenannt'}`,
      {
        unit_id: unitData.id,
        unit_number: unitData.unit_number,
        property_id: unitData.property_id
      }
    );
  }

  async logUnitDeleted(unitData) {
    return this.logActivity(
      'einheiten',
      'gelöscht',
      `Einheit gelöscht: ${unitData.unit_number || 'Unbenannt'}`,
      {
        unit_id: unitData.id,
        unit_number: unitData.unit_number,
        property_id: unitData.property_id
      }
    );
  }

  // === MIETER ===
  async logTenantCreated(tenantData) {
    return this.logActivity(
      'mieter',
      'erstellt',
      `Neuer Mieter angelegt: ${tenantData.first_name} ${tenantData.last_name}`,
      {
        tenant_id: tenantData.id,
        name: `${tenantData.first_name} ${tenantData.last_name}`,
        property_id: tenantData.property_id,
        unit_id: tenantData.unit_id
      }
    );
  }

  async logTenantUpdated(tenantData) {
    return this.logActivity(
      'mieter',
      'bearbeitet',
      `Mieter bearbeitet: ${tenantData.first_name} ${tenantData.last_name}`,
      {
        tenant_id: tenantData.id,
        name: `${tenantData.first_name} ${tenantData.last_name}`,
        property_id: tenantData.property_id,
        unit_id: tenantData.unit_id
      }
    );
  }

  async logTenantDeleted(tenantData) {
    return this.logActivity(
      'mieter',
      'gelöscht',
      `Mieter gelöscht: ${tenantData.first_name} ${tenantData.last_name}`,
      {
        tenant_id: tenantData.id,
        name: `${tenantData.first_name} ${tenantData.last_name}`,
        property_id: tenantData.property_id,
        unit_id: tenantData.unit_id
      }
    );
  }

  // === EIGENTÜMER ===
  async logOwnerCreated(ownerData) {
    return this.logActivity(
      'eigentümer',
      'erstellt',
      `Neuer Eigentümer angelegt: ${ownerData.first_name} ${ownerData.last_name}`,
      {
        owner_entity: ownerData.id,
        name: `${ownerData.first_name} ${ownerData.last_name}`,
        company: ownerData.company
      }
    );
  }

  async logOwnerUpdated(ownerData) {
    return this.logActivity(
      'eigentümer',
      'bearbeitet',
      `Eigentümer bearbeitet: ${ownerData.first_name} ${ownerData.last_name}`,
      {
        owner_entity: ownerData.id,
        name: `${ownerData.first_name} ${ownerData.last_name}`,
        company: ownerData.company
      }
    );
  }

  async logOwnerDeleted(ownerData) {
    return this.logActivity(
      'eigentümer',
      'gelöscht',
      `Eigentümer gelöscht: ${ownerData.first_name} ${ownerData.last_name}`,
      {
        owner_entity: ownerData.id,
        name: `${ownerData.first_name} ${ownerData.last_name}`,
        company: ownerData.company
      }
    );
  }

  // === FINANZIERUNGEN ===
  async logFinancingCreated(financingData) {
    return this.logActivity(
      'finanzierungen',
      'erstellt',
      `Neue Finanzierung angelegt: ${financingData.bank_name || 'Unbenannt'} (${financingData.loan_amount}€)`,
      {
        financing_id: financingData.id,
        bank_name: financingData.bank_name,
        loan_amount: financingData.loan_amount,
        property_id: financingData.property_id
      }
    );
  }

  async logFinancingUpdated(financingData) {
    return this.logActivity(
      'finanzierungen',
      'bearbeitet',
      `Finanzierung bearbeitet: ${financingData.bank_name || 'Unbenannt'} (${financingData.loan_amount}€)`,
      {
        financing_id: financingData.id,
        bank_name: financingData.bank_name,
        loan_amount: financingData.loan_amount,
        property_id: financingData.property_id
      }
    );
  }

  async logFinancingDeleted(financingData) {
    return this.logActivity(
      'finanzierungen',
      'gelöscht',
      `Finanzierung gelöscht: ${financingData.bank_name || 'Unbenannt'} (${financingData.loan_amount}€)`,
      {
        financing_id: financingData.id,
        bank_name: financingData.bank_name,
        loan_amount: financingData.loan_amount,
        property_id: financingData.property_id
      }
    );
  }

  // === DOKUMENTE ===
  async logDocumentUploaded(documentData) {
    return this.logActivity(
      'dokumente',
      'hochgeladen',
      `Dokument hochgeladen: ${documentData.filename || 'Unbenannt'}`,
      {
        document_id: documentData.id,
        filename: documentData.filename,
        category: documentData.category,
        property_id: documentData.property_id,
        tenant_id: documentData.tenant_id
      }
    );
  }

  async logDocumentDeleted(documentData) {
    return this.logActivity(
      'dokumente',
      'gelöscht',
      `Dokument gelöscht: ${documentData.filename || 'Unbenannt'}`,
      {
        document_id: documentData.id,
        filename: documentData.filename,
        category: documentData.category,
        property_id: documentData.property_id,
        tenant_id: documentData.tenant_id
      }
    );
  }
}

// Singleton-Instanz exportieren
const activityService = new ActivityService();
export default activityService;

